import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { ContragentTypeEnum, MtrTypeEnum } from 'gql/graphql';

import { useDebounce } from '@consta/uikit/useDebounce';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { uerIsUniquePriceListMtrRowCodeGql } from '../workOrderQueries';

const requestId = 'uerIsUniquePriceListMtrCode';

interface IUseIsUniquePriceListMtrCode {
    contractId: number;
    mtrRowId: number | null;
    supplyBy: ContragentTypeEnum;
    mtrType: MtrTypeEnum;
    code: string | null;
    isDirtyCodeValue: boolean;
}

export const useIsUniquePriceListMtrCode = ({
    contractId,
    isDirtyCodeValue,
    code,
    mtrRowId,
    mtrType,
    supplyBy,
}: IUseIsUniquePriceListMtrCode) => {
    const dispatch = useDispatch();

    const [data, status, err, request] = useRequest({
        requestId,
        query: uerIsUniquePriceListMtrRowCodeGql,
        withEffect: false,
    });

    const variables = useMemo(
        () =>
            Boolean(contractId && mtrType && supplyBy) && code?.length
                ? { contractId, mtrRowId, mtrType, supplyBy, inputCode: code }
                : undefined,
        [code, contractId, mtrRowId, mtrType, supplyBy],
    );

    const debounceRequest = useDebounce(() => {
        if (supplyBy && mtrType && code && isDirtyCodeValue) {
            dispatch(request(variables));
        }
    }, 300);

    const [uerIsUniqueContractObjectCodeReset] = useResetStoreData({ requestId });

    useEffect(() => {
        const isCodeExistAndNonZero = code && code.split(' ').length === 1 && code !== '0000';

        if (isCodeExistAndNonZero && mtrType && supplyBy) {
            debounceRequest();
        }
    }, [code, debounceRequest, mtrType, supplyBy]);

    useEffect(
        () => () => {
            uerIsUniqueContractObjectCodeReset(undefined);
        },
        [uerIsUniqueContractObjectCodeReset],
    );

    return {
        result: data?.uerIsUniquePriceListMtrRowCode?.result ?? true,
        status,
        err,
    };
};
